import React, { useState } from 'react';
import '../LandingPage.css';

const LandingPage = ({ onLoginSuccess }) => {
  const [showSignIn, setShowSignIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch('http://localhost:5000/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Sign in failed');
        return;
      }

      // Store token
      localStorage.setItem('token', data.token);
      localStorage.setItem('userId', data.user_id);
      localStorage.setItem('user', JSON.stringify({
        id: data.user_id,
        email: data.email,
        role: data.role,
      }));

      // Call callback to update parent component
      if (onLoginSuccess) {
        onLoginSuccess(data.token, data.user_id);
      } else {
        // Fallback: reload page
        window.location.reload();
      }
    } catch (err) {
      setError('Connection error. Please try again.');
      console.error('Sign in error:', err);
    } finally {
      setLoading(false);
    }
  };

  const stages = [
    { num: 1, title: 'Intake', desc: 'Define the problem or opportunity' },
    { num: 2, title: 'Framing', desc: 'Frame the challenge with context' },
    { num: 3, title: 'Hypotheses', desc: 'Develop testable solutions' },
    { num: 4, title: 'Experiment', desc: 'Test and validate ideas' },
    { num: 5, title: 'Decision', desc: 'Make go/no-go decisions' },
  ];

  const benefits = [
    { icon: '📋', title: 'Structured Process', desc: 'Guide teams through proven innovation methodology' },
    { icon: '💡', title: 'Idea Collaboration', desc: 'Capture and refine ideas from across your organization' },
    { icon: '💬', title: 'Real-time Discussion', desc: 'Debate and build consensus on solutions' },
    { icon: '👥', title: 'Role-Based Access', desc: 'Control who participates and contributes at each stage' },
    { icon: '📊', title: 'Clear Decisions', desc: 'Move from ideas to decisions with documented reasoning' },
    { icon: '🎯', title: 'Track Progress', desc: 'Visualize your innovation pipeline end-to-end' },
  ];

  return (
    <div className="landing-page">
      {/* Header */}
      <header className="landing-header">
        <div className="header-container">
          <div className="logo-section">
            <img src="/ontario-consulting-logo.svg" alt="Ontario Consulting" className="logo-image" />
          </div>
          <button
            className="signin-btn-header"
            onClick={() => setShowSignIn(true)}
          >
            Sign In
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1 className="hero-title">Transform Ideas Into Innovation</h1>
          <p className="hero-subtitle">
            A structured platform that guides your team through innovation with clarity,
            collaboration, and data-driven decisions.
          </p>
          <button
            className="hero-cta"
            onClick={() => setShowSignIn(true)}
          >
            Get Started
          </button>
        </div>
        <div className="hero-visual">
          <div className="hero-circle"></div>
        </div>
      </section>

      {/* Problem/Solution */}
      <section className="problem-solution">
        <div className="container">
          <div className="ps-grid">
            <div className="ps-card problem">
              <h3>The Challenge</h3>
              <ul>
                <li>Ideas get lost in conversations and emails</li>
                <li>No clear process for moving from problem to solution</li>
                <li>Difficulty achieving team alignment</li>
                <li>Hard to track which ideas were tested and why decisions were made</li>
              </ul>
            </div>
            <div className="ps-divider"></div>
            <div className="ps-card solution">
              <h3>The Boston Project Solution</h3>
              <ul>
                <li>Structured 5-stage innovation workflow</li>
                <li>Centralized idea capture and collaboration</li>
                <li>Clear roles and permissions for teams</li>
                <li>Documented decision trail for accountability</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <div className="container">
          <h2>The Innovation Journey</h2>
          <p className="section-subtitle">
            Five proven stages to move from challenge to decision
          </p>

          <div className="stages-container">
            {stages.map((stage, idx) => (
              <div key={idx} className="stage-card">
                <div className="stage-number">{stage.num}</div>
                <h4>{stage.title}</h4>
                <p>{stage.desc}</p>
                {idx < stages.length - 1 && <div className="stage-arrow">→</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="benefits">
        <div className="container">
          <h2>Why Teams Choose The Boston Project</h2>
          <div className="benefits-grid">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="benefit-card">
                <div className="benefit-icon">{benefit.icon}</div>
                <h4>{benefit.title}</h4>
                <p>{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to transform your innovation process?</h2>
          <button className="cta-btn" onClick={() => setShowSignIn(true)}>
            Start Your Journey
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-left">
              <div className="footer-logo">Ontario Consulting</div>
              <p className="footer-description">
                Ontario Consulting is a management consulting company based in Toronto, specialized in Agile Transformation and AI Consulting. We deliver customer-tailored solutions that help organizations adapt modern ways of working.
              </p>
            </div>

            <div className="footer-section">
              <h4>Quick Links</h4>
              <ul>
                <li><a href="https://ontarioconsulting.ca/about" target="_blank" rel="noopener noreferrer">About us</a></li>
                <li><a href="https://ontarioconsulting.ca/academy" target="_blank" rel="noopener noreferrer">Academy</a></li>
                <li><a href="https://ontarioconsulting.ca/careers" target="_blank" rel="noopener noreferrer">Careers</a></li>
                <li><a href="https://ontarioconsulting.ca/team" target="_blank" rel="noopener noreferrer">Our Team</a></li>
                <li><a href="https://ontarioconsulting.ca/faq" target="_blank" rel="noopener noreferrer">FAQ</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Our Services</h4>
              <ul>
                <li><a href="https://ontarioconsulting.ca/services/ai-adoption" target="_blank" rel="noopener noreferrer">AI Adoption</a></li>
                <li><a href="https://ontarioconsulting.ca/services/agile-transformation" target="_blank" rel="noopener noreferrer">Agile Transformation</a></li>
                <li><a href="https://ontarioconsulting.ca/services/human-centric-design" target="_blank" rel="noopener noreferrer">Human Centric Design</a></li>
                <li><a href="https://ontarioconsulting.ca/services/digital-data-practice" target="_blank" rel="noopener noreferrer">Digital & Data Practice</a></li>
                <li><a href="https://ontarioconsulting.ca/services/operational-excellence" target="_blank" rel="noopener noreferrer">Operational Excellence</a></li>
              </ul>
            </div>

            <div className="footer-section">
              <h4>Contact</h4>
              <ul className="contact-list">
                <li>📍 Toronto, Ontario, Canada</li>
                <li>📧 <a href="mailto:info@ontarioconsulting.ca">info@ontarioconsulting.ca</a></li>
                <li>📱 <a href="tel:+19058085042">+1 905 808 5042</a></li>
              </ul>
              <div className="social-links">
                <a href="https://www.linkedin.com/company/ontarioconsulting/posts/?feedView=all" target="_blank" rel="noopener noreferrer" title="LinkedIn">in</a>
                <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" title="YouTube">▶</a>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p>© 2026 Ontario Consulting. All rights reserved.</p>
            <div className="footer-legal">
              <a href="#terms">Terms & Conditions</a>
              <a href="#privacy">Privacy Policy</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Sign In Modal */}
      {showSignIn && (
        <div className="signin-overlay" onClick={() => setShowSignIn(false)}>
          <div className="signin-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-btn" onClick={() => setShowSignIn(false)}>
              ✕
            </button>

            <div className="signin-header">
              <div className="signin-logo-circle">
                <div className="circle circle-1"></div>
                <div className="circle circle-2"></div>
                <div className="circle circle-3"></div>
              </div>
              <h2>Sign In</h2>
              <p>Access your innovation journeys</p>
            </div>

            <form onSubmit={handleSignIn} className="signin-form">
              {error && <div className="signin-error">{error}</div>}

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  required
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>

              <button type="submit" className="signin-submit" disabled={loading}>
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            <div className="signin-footer">
              <p>Demo credentials available upon request</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
